import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtL, fmtMon, apiErr } from "@/lib/format";
import { useModalForm } from "@/lib/modalForm";
import { XIcon } from "@phosphor-icons/react";

export const METODOS_PAGO = [
  { v: "transferencia", l: "Transferencia" },
  { v: "cheque", l: "Cheque" },
  { v: "efectivo", l: "Efectivo" },
  { v: "deposito", l: "Depósito" },
  { v: "tarjeta", l: "Tarjeta débito" },
  { v: "tarjeta_credito", l: "Tarjeta de crédito" },
  { v: "nota_credito", l: "Nota de crédito" },
];

// Cuentas de tesorería válidas para el método elegido
export const cuentasPara = (accounts, metodo) => {
  if (metodo === "efectivo") return accounts.filter((a) => a.tipo === "caja");
  if (metodo === "tarjeta_credito") return accounts.filter((a) => a.tipo === "tarjeta");
  if (metodo === "nota_credito") return [];
  return accounts.filter((a) => a.tipo !== "caja");
};

export const monedaDe = (f) => (f?.moneda || "HNL").toUpperCase();
export const saldoMoneda = (f) => Number(f?.saldo_moneda ?? f?.saldo_pendiente ?? 0);
export const ESTADO_PAGO = { emitida: "Pendiente", parcial: "Parcialmente pagada", pagada: "Pagada", anulada: "Anulada" };

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const hoy = () => new Date().toISOString().slice(0, 10);
const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{children}</label>;
const inp = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm";

/**
 * Pago de una factura de compra en lempiras o dólares. La moneda del pago es la de la cuenta de tesorería elegida.
 * Si la factura o el pago están en dólares se pide la tasa del pago (lempiras por dólar); se muestra lo que el pago
 * cancela en la moneda de la factura y el diferencial cambiario frente a la tasa original (que no cambia).
 */
export default function PagoCompraModal({ factura, accounts, onClose, onSaved }) {
  const mf = monedaDe(factura);
  const tcf = Number(factura.tipo_cambio || 1);
  const [pay, setPay] = useState({ monto: mf === "HNL" ? r2(factura.saldo_pendiente) : "", metodo: "transferencia", cuenta_id: "",
    referencia: "", banco: "", notas: "", fecha: hoy(), retencion_isr: "", retencion_isv: "", credit_note_id: "", tipo_cambio: mf === "USD" ? String(tcf) : "" });
  const [ncs, setNcs] = useState([]);
  const [idem] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  useEffect(() => {
    // solo notas de crédito de la misma subcuenta (no se compensan saldos entre países)
    api.get("/purchase-credit-notes", { params: { proveedor_id: factura.proveedor_id, disponibles: true, subcuenta_id: factura.subcuenta_id || "" } })
      .then(({ data }) => setNcs(data || [])).catch(() => setNcs([]));
  }, [factura.proveedor_id, factura.subcuenta_id]);

  const cuenta = accounts.find((a) => a.id === pay.cuenta_id);
  const mp = pay.metodo === "nota_credito" ? "HNL" : ((cuenta?.moneda || "HNL").toUpperCase());
  const usaUsd = mp === "USD" || mf === "USD";
  const tcp = Number(pay.tipo_cambio) || 0;
  const monto = Number(pay.monto) || 0;
  const saldoM = saldoMoneda(factura);
  // misma conversión que el servidor
  const calc = useMemo(() => {
    let cancela = monto;
    if (mp !== mf) cancela = mf === "USD" ? (tcp ? monto / tcp : 0) : monto * tcp;
    cancela = r2(cancela);
    const final = cancela >= saldoM - 0.01;
    const pagoHnl = mp === "USD" ? r2(monto * tcp) : r2(monto);
    let libro = pagoHnl;
    if (usaUsd) libro = final ? r2(factura.saldo_pendiente) : (mf === "USD" ? r2(Math.min(cancela, saldoM) * tcf) : cancela);
    return { cancela: final ? saldoM : cancela, pagoHnl, libro, dif: usaUsd ? r2(pagoHnl - libro) : 0, excede: cancela > saldoM + 0.01 };
  }, [monto, mp, mf, tcp, tcf, saldoM, usaUsd, factura.saldo_pendiente]);

  const submit = async (e) => {
    e.preventDefault();
    if (pay.metodo === "nota_credito") {
      if (!pay.credit_note_id) return toast.error("Selecciona la nota de crédito a aplicar");
    } else {
      if (!pay.referencia.trim()) return toast.error("Referencia obligatoria (cheque, transferencia, recibo)");
      if (pay.metodo !== "tarjeta_credito" && !pay.cuenta_id) return toast.error("Selecciona la cuenta de tesorería");
    }
    if (usaUsd && !(tcp > 0)) return toast.error("Indica la tasa de cambio del pago (lempiras por cada dólar)");
    if (calc.excede) return toast.error(`El pago cancela ${fmtMon(calc.cancela, mf)} y el saldo es ${fmtMon(saldoM, mf)}`);
    try {
      await api.post("/purchase-payments", {
        factura_id: factura.id, monto, moneda: mp, tipo_cambio: usaUsd ? tcp : null, idempotency_key: idem,
        metodo: pay.metodo, cuenta_id: pay.cuenta_id || null, referencia: pay.referencia, banco: pay.banco, notas: pay.notas,
        fecha: pay.fecha || null, retencion_isr: Number(pay.retencion_isr) || 0, retencion_isv: Number(pay.retencion_isv) || 0,
        credit_note_id: pay.metodo === "nota_credito" ? pay.credit_note_id : null,
      });
      toast.success(`Pago a ${factura.proveedor_nombre} registrado · CxP, banco y asiento actualizados`);
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const pm = useModalForm({ value: pay, abierto: true, onClose, onSave: submit });
  const retTotal = (Number(pay.retencion_isr) || 0) + (Number(pay.retencion_isv) || 0);
  const cuentas = cuentasPara(accounts, pay.metodo).filter((a) => a.activo !== false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...pm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="payable-payment-modal">
        <div className="p-5 border-b border-border flex justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Pagar a proveedor</div>
            <h3 className="font-heading text-lg font-semibold">{factura.numero_interno}</h3>
            <div className="text-xs text-text-secondary">{factura.subcuenta_nombre || factura.proveedor_nombre}{factura.branch_codigo ? ` · ${factura.branch_codigo}` : ""}</div>
          </div>
          <button aria-label="Cerrar" onClick={pm.cerrar} className="text-text-muted"><XIcon size={20} /></button>
        </div>
        <form onSubmit={pm.guardar} className="p-5 space-y-4">
          <div className="bg-app border border-border rounded-md p-3 text-sm space-y-1" data-testid="pago-saldo">
            <div className="flex justify-between items-center"><span className="text-text-secondary">Saldo pendiente</span>
              <span className="font-heading text-lg text-agro-yellow">{fmtMon(saldoM, mf)}</span></div>
            {mf === "USD" && <div className="flex justify-between text-xs text-text-muted"><span>Factura en dólares · tasa original {tcf}</span>
              <span>CxP contable {fmtL(factura.saldo_pendiente)}</span></div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Método *</Lbl>
              <select value={pay.metodo} onChange={(e) => setPay({ ...pay, metodo: e.target.value, cuenta_id: "" })} data-testid="payable-metodo" className={inp}>
                {METODOS_PAGO.filter((m) => mf === "HNL" || m.v !== "nota_credito").map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
            </div>
            {pay.metodo === "nota_credito" ? (
              <div>
                <Lbl>Nota de crédito *</Lbl>
                <select value={pay.credit_note_id} onChange={(e) => {
                  const nc = ncs.find((x) => x.id === e.target.value);
                  setPay({ ...pay, credit_note_id: e.target.value, referencia: nc ? nc.numero : pay.referencia,
                    monto: nc ? Math.min(Number(factura.saldo_pendiente), Number(nc.saldo_disponible)) : pay.monto });
                }} data-testid="payable-nc" className={inp}>
                  <option value="">{ncs.length ? "— Selecciona —" : "Sin NC disponibles"}</option>
                  {ncs.map((n) => <option key={n.id} value={n.id}>{n.numero} · disp. {fmtL(n.saldo_disponible)}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <Lbl>{pay.metodo === "tarjeta_credito" ? "Tarjeta (opcional)" : "Cuenta de tesorería *"}</Lbl>
                <select value={pay.cuenta_id} onChange={(e) => setPay({ ...pay, cuenta_id: e.target.value })} data-testid="payable-cuenta" className={inp}>
                  <option value="">{pay.metodo === "tarjeta_credito" ? "Tarjetas de crédito por pagar" : "— Selecciona —"}</option>
                  {cuentas.map((a) => <option key={a.id} value={a.id}>{(a.moneda || "HNL")} · {a.codigo} · {a.nombre} ({fmtMon(a.saldo_actual, a.moneda)})</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Lbl>Importe pagado ({mp === "USD" ? "US$" : "L."}) *</Lbl>
              <input type="number" step="0.01" value={pay.monto} onChange={(e) => setPay({ ...pay, monto: e.target.value })} required data-testid="payable-amount"
                className={`${inp} text-base focus:border-agro-green`} />
            </div>
            <div>
              <Lbl>Fecha de pago *</Lbl>
              <input type="date" value={pay.fecha} onChange={(e) => setPay({ ...pay, fecha: e.target.value })} required data-testid="payable-fecha" className={inp} />
            </div>
          </div>
          {usaUsd && (
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Lbl>Tasa de cambio del pago (L por US$) *</Lbl>
                <input type="number" step="0.0001" min="0" value={pay.tipo_cambio} onChange={(e) => setPay({ ...pay, tipo_cambio: e.target.value })}
                  data-testid="payable-tasa" className={inp} />
              </div>
              <div className="text-[11px] text-text-muted pb-2">La tasa de la factura ({mf === "USD" ? tcf : "—"}) no cambia; cada pago guarda la suya.</div>
            </div>
          )}
          {monto > 0 && (mp !== "HNL" || mf !== "HNL") && (
            <div className="text-xs bg-agro-green/5 border border-agro-green/30 rounded p-3 space-y-1" data-testid="pago-conversion">
              {mp === "HNL" && mf === "USD" && <div>{fmtL(monto)} ÷ {tcp || "—"} = cancela <b>{fmtMon(calc.cancela, "USD")}</b> de la factura</div>}
              {mp === "USD" && <div>{fmtMon(monto, "USD")} × {tcp || "—"} = equivalente contable <b>{fmtL(calc.pagoHnl)}</b>{mf === "HNL" && <> · cancela <b>{fmtL(calc.cancela)}</b></>}</div>}
              {mf === "USD" && <div>Valor en la CxP a la tasa de la factura: {fmtL(calc.libro)}</div>}
              {Math.abs(calc.dif) >= 0.01 && <div className={calc.dif > 0 ? "text-red-300" : "text-agro-green"} data-testid="pago-diferencial">
                Diferencial cambiario: {calc.dif > 0 ? "pérdida" : "ganancia"} {fmtL(Math.abs(calc.dif))}</div>}
              {calc.excede && <div className="text-red-300">Supera el saldo ({fmtMon(saldoM, mf)}).</div>}
            </div>
          )}
          {pay.metodo !== "nota_credito" && !usaUsd && (
            <div className="grid grid-cols-2 gap-3">
              <div><Lbl>ISR retenido (L.)</Lbl><input type="number" step="0.01" min="0" value={pay.retencion_isr} onChange={(e) => setPay({ ...pay, retencion_isr: e.target.value })} data-testid="payable-ret-isr" className={inp} /></div>
              <div><Lbl>ISV retenido (L.)</Lbl><input type="number" step="0.01" min="0" value={pay.retencion_isv} onChange={(e) => setPay({ ...pay, retencion_isv: e.target.value })} data-testid="payable-ret-isv" className={inp} /></div>
            </div>
          )}
          {retTotal > 0 && (
            <div className="text-xs text-text-secondary bg-agro-yellow/5 border border-agro-yellow/30 rounded p-2">
              Salida de dinero: <b className="text-agro-yellow">{fmtL(monto - retTotal)}</b> · Se emitirá constancia de retención por {fmtL(retTotal)}.
            </div>
          )}
          <div>
            <Lbl>Referencia (cheque, transferencia, recibo) *</Lbl>
            <input value={pay.referencia} onChange={(e) => setPay({ ...pay, referencia: e.target.value })} required={pay.metodo !== "nota_credito"} data-testid="payable-referencia"
              placeholder="ej. CHQ-12345 / TRF-789" className={`${inp} focus:border-agro-green`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Lbl>Banco</Lbl><input value={pay.banco} onChange={(e) => setPay({ ...pay, banco: e.target.value })} className={inp} /></div>
            <div><Lbl>Notas</Lbl><input value={pay.notas} onChange={(e) => setPay({ ...pay, notas: e.target.value })} className={inp} /></div>
          </div>
          <button disabled={pm.saving} type="submit" data-testid="payable-submit" className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 disabled:opacity-50">
            {pm.saving ? "Registrando…" : "Registrar pago"}
          </button>
        </form>
      </div>
    </div>
  );
}

/** Pagos aplicados a una factura, con su moneda y su tasa (distintas de la conversión original de la factura). */
export function PagosFactura({ inv }) {
  const mf = monedaDe(inv);
  const pagos = inv.pagos || [];
  return (
    <div className="mt-4" data-testid="pi-pagos">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Pagos aplicados ({pagos.length})</div>
      {pagos.length === 0 ? <div className="text-xs text-text-muted">Sin pagos aplicados.</div> : (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
            <th className="p-1.5">Fecha</th><th className="p-1.5">Referencia · cuenta</th><th className="p-1.5 text-right">Pagado</th>
            <th className="p-1.5 text-right">Tasa del pago</th><th className="p-1.5 text-right">Equivalente L.</th>
            <th className="p-1.5 text-right">Cancela ({mf === "USD" ? "US$" : "L."})</th><th className="p-1.5 text-right">CxP (L.)</th><th className="p-1.5 text-right">Dif. cambiaria</th>
          </tr></thead>
          <tbody>
            {pagos.map((p) => {
              const mp = p.moneda_pago || "HNL";
              const dif = Number(p.diferencial_cambiario || 0);
              return (
                <tr key={p.id} className="border-b border-border/30" data-testid={`pi-pago-${p.referencia}`}>
                  <td className="p-1.5">{String(p.fecha || "").slice(0, 10)}</td>
                  <td className="p-1.5">{p.referencia}<div className="text-[10px] text-text-muted">{p.metodo}{p.retencion_isr || p.retencion_isv ? " · con retención" : ""}</div></td>
                  <td className="p-1.5 text-right">{fmtMon(p.monto_moneda_pago || p.monto, mp)}</td>
                  <td className="p-1.5 text-right">{p.tipo_cambio_pago || "—"}</td>
                  <td className="p-1.5 text-right">{fmtL(p.monto_hnl_pago || p.monto)}</td>
                  <td className="p-1.5 text-right font-medium">{fmtMon(p.cancelado_moneda_factura || p.monto, mf)}</td>
                  <td className="p-1.5 text-right">{fmtL(p.monto)}</td>
                  <td className={`p-1.5 text-right ${dif > 0 ? "text-red-300" : dif < 0 ? "text-agro-green" : "text-text-muted"}`}>
                    {Math.abs(dif) >= 0.01 ? `${dif > 0 ? "Pérdida" : "Ganancia"} ${fmtL(Math.abs(dif))}` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Historial de ediciones (solo Administrador): usuario, fecha, motivo y valores anteriores / nuevos. */
export function HistorialEdiciones({ inv }) {
  const eds = inv.ediciones || [];
  if (!eds.length) return null;
  const txt = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));
  return (
    <div className="mt-4" data-testid="pi-ediciones">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Historial de ediciones ({eds.length})</div>
      <div className="space-y-2">
        {eds.slice().reverse().map((e) => (
          <div key={e.id} className="border border-border rounded p-2 text-xs">
            <div className="flex justify-between"><b>{e.tipo === "correccion_completa" ? "Corrección de importes / detalle" : "Datos del documento"}</b>
              <span className="text-text-muted">{String(e.fecha).slice(0, 16).replace("T", " ")} · {e.usuario_nombre || e.usuario}</span></div>
            <div className="text-text-secondary">Motivo: {e.motivo}</div>
            <ul className="mt-1 space-y-0.5">
              {Object.entries(e.cambios || {}).filter(([k]) => k !== "lineas").map(([k, v]) => (
                <li key={k} className="text-[11px]"><span className="text-text-muted">{k}:</span> {txt(v.antes)} → <b>{txt(v.despues)}</b></li>
              ))}
              {e.cambios?.lineas && <li className="text-[11px] text-text-muted">Líneas modificadas (cantidades / precios)</li>}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
